import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  NetworkIcon,
  GlobeIcon,
  SettingsIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  Loader2Icon,
  Trash2Icon,
  PlusIcon,
  ShieldIcon,
  ShieldOffIcon,
  XIcon,
  PencilIcon,
  InfoIcon,
} from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { apiClient } from '@nebula/shared/services/api'
import instancesApi, {
  type VMInstanceDetails,
  type VMNICInfo,
} from '@nebula/shared/services/instancesApi'
import { getPublicIPStatusColor, getPublicIPStatusLabel } from '@nebula/shared/services/publicIpApi'
import { listCustomerVPCs, type VPCListItem } from '@nebula/shared/services/vpcApi'
import { formatRecurringPrice } from '@nebula/shared/utils/format'

import NetworkModeCard from './NetworkModeCard'

type NetworkTabProps = {
  instance: VMInstanceDetails
}

type StaticIPConfig = {
  ip_address: string
  gateway: string
  dns_servers: string[]
  hostname: string
  interface: string
}

export default function NetworkTab({ instance }: NetworkTabProps) {
  const queryClient = useQueryClient()

  // NIC management state
  const [showAddNIC, setShowAddNIC] = useState(false)
  const [selectedVPCId, setSelectedVPCId] = useState('')
  const [nicFirewall, setNicFirewall] = useState(true)
  const [removingNic, setRemovingNic] = useState<string | null>(null)
  const [editingNic, setEditingNic] = useState<string | null>(null)
  const [editVPCId, setEditVPCId] = useState('')
  const [editFirewall, setEditFirewall] = useState(true)

  // Static IP configuration state
  const [showConfig, setShowConfig] = useState(false)
  const [config, setConfig] = useState<StaticIPConfig>({
    ip_address: '',
    gateway: '',
    dns_servers: ['8.8.8.8', '8.8.4.4'],
    hostname: instance.name || '',
    interface: 'eth0',
  })
  const [dnsInput, setDnsInput] = useState('8.8.8.8, 8.8.4.4')
  const [configSuccess, setConfigSuccess] = useState(false)
  const [configResult, setConfigResult] = useState<any>(null)

  // NICs e detalhe da instância na mesma invalidação: o card IPv4 do topo lê o
  // `public_ip` do detalhe, e sem isto ele só acompanharia a mudança de NIC no refetch
  // de 30s. A chave que estava aqui antes (`instance-details`) não é registrada por
  // nenhuma query do monorepo — invalidá-la nunca fez nada.
  const invalidateVmQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['instance-nics', instance.id] })
    queryClient.invalidateQueries({ queryKey: ['instance', instance.id] })
  }

  // Query: list NICs
  const {
    data: nicsData,
    isLoading: nicsLoading,
    isError: nicsError,
  } = useQuery({
    queryKey: ['instance-nics', instance.id],
    queryFn: () => instancesApi.listNICs(instance.id),
    enabled: !!instance.id,
  })

  // Query: list customer VPCs (for add/edit NIC selector)
  const { data: vpcsData } = useQuery({
    queryKey: ['customer-vpcs'],
    queryFn: () => listCustomerVPCs(),
    enabled: showAddNIC || editingNic !== null,
  })

  // Mutation: add NIC
  const addNICMutation = useMutation({
    mutationFn: (data: { network_id: string; firewall: boolean }) =>
      instancesApi.addNIC(instance.id, data),
    onSuccess: () => {
      invalidateVmQueries()
      setShowAddNIC(false)
      setSelectedVPCId('')
      setNicFirewall(true)
    },
  })

  // Mutation: remove NIC
  const removeNICMutation = useMutation({
    mutationFn: (netId: string) => instancesApi.removeNIC(instance.id, netId),
    onSuccess: () => {
      invalidateVmQueries()
      setRemovingNic(null)
    },
    onError: () => {
      setRemovingNic(null)
    },
  })

  // Mutation: update NIC
  const updateNICMutation = useMutation({
    mutationFn: (data: { netId: string; network_id?: string; firewall?: boolean }) =>
      instancesApi.updateNIC(instance.id, data.netId, {
        network_id: data.network_id,
        firewall: data.firewall,
      }),
    onSuccess: () => {
      invalidateVmQueries()
      setEditingNic(null)
    },
  })

  // Mutation: configure static IP
  const configureIPMutation = useMutation({
    mutationFn: async (data: StaticIPConfig) => {
      const response = await apiClient.post(
        `/api/v1/end-customer/instances/${instance.id}/network/static`,
        data,
      )
      return response
    },
    onSuccess: (data) => {
      setConfigSuccess(true)
      setConfigResult(data)
      invalidateVmQueries()
    },
  })

  // Mutation: remove cloud-init
  const removeCloudInitMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.delete(
        `/api/v1/end-customer/instances/${instance.id}/network/cloudinit`,
      )
      return response
    },
    onSuccess: () => {
      invalidateVmQueries()
      setConfigSuccess(false)
      setConfigResult(null)
    },
  })

  const isValidIP = (ip: string): boolean => {
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/
    if (!ipRegex.test(ip)) return false
    const parts = ip.split('.')
    return parts.every((part) => Number.parseInt(part) >= 0 && Number.parseInt(part) <= 255)
  }

  const isValidCIDR = (cidr: string): boolean => {
    const parts = cidr.split('/')
    if (parts.length !== 2) return false
    if (!isValidIP(parts[0])) return false
    const prefix = Number.parseInt(parts[1])
    return prefix >= 0 && prefix <= 32
  }

  const isFormValid = () => {
    return (
      isValidCIDR(config.ip_address) && isValidIP(config.gateway) && config.interface.length > 0
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const dnsServers = dnsInput
      .split(',')
      .map((s) => s.trim())
      .filter((s) => isValidIP(s))

    configureIPMutation.mutate({
      ...config,
      dns_servers: dnsServers.length > 0 ? dnsServers : ['8.8.8.8'],
    })
  }

  const handleReset = () => {
    setShowConfig(false)
    setConfigSuccess(false)
    setConfigResult(null)
    setConfig({
      ip_address: '',
      gateway: '',
      dns_servers: ['8.8.8.8', '8.8.4.4'],
      hostname: instance.name || '',
      interface: 'eth0',
    })
    setDnsInput('8.8.8.8, 8.8.4.4')
  }

  const handleAddNIC = () => {
    if (!selectedVPCId) return
    addNICMutation.mutate({ network_id: selectedVPCId, firewall: nicFirewall })
  }

  const handleRemoveNIC = (netId: string) => {
    setRemovingNic(netId)
    removeNICMutation.mutate(netId)
  }

  const handleStartEdit = (nic: VMNICInfo) => {
    setEditingNic(nic.net_id)
    setEditVPCId(nic.vpc?.id || '')
    setEditFirewall(nic.firewall)
    updateNICMutation.reset()
  }

  const handleCancelEdit = () => {
    setEditingNic(null)
    updateNICMutation.reset()
  }

  const handleSaveEdit = () => {
    if (!editingNic) return
    const currentNic = nicsData?.nics.find((n) => n.net_id === editingNic)
    const changedVPC = editVPCId && editVPCId !== (currentNic?.vpc?.id || '')
    const changedFirewall = editFirewall !== currentNic?.firewall

    if (!changedVPC && !changedFirewall) {
      setEditingNic(null)
      return
    }

    updateNICMutation.mutate({
      netId: editingNic,
      network_id: changedVPC ? editVPCId : undefined,
      firewall: changedFirewall ? editFirewall : undefined,
    })
  }

  // O detalhe da instância é o fallback SÓ enquanto o endpoint de NICs não respondeu
  // (Proxmox fora, VM sumida do node): o IP segue reservado e cobrado, e o cliente
  // precisa vê-lo assim mesmo. Quando `/nics` responde, ele é a fonte — um
  // `public_ip: null` de lá significa "saiu desta VM" e não pode ser sobreposto por
  // um detalhe em cache, que mostraria cobrança de um IP alheio à VM.
  const publicIp =
    nicsError || !nicsData ? (instance.public_ip ?? null) : (nicsData.public_ip ?? null)
  const publicIpPrice = publicIp
    ? formatRecurringPrice(publicIp.unit_price, publicIp.billing_cycle)
    : null

  return (
    <div className="space-y-6">
      {/* Modo de rede — issue #761. Fica acima do IP público de propósito: é ele
          que decide se a VM TEM um IPv4 público, então lê-se a causa antes do
          efeito. */}
      <NetworkModeCard instance={instance} />

      {/* IP público fixo — issue #733.
          Vem do banco (não do guest agent), então continua visível com a VM parada:
          o IP segue reservado e cobrado mesmo assim. */}
      {publicIp && (
        <div className="rounded-lg border-l-4 border-blue-500 bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center text-lg font-semibold text-gray-900">
              <GlobeIcon className="mr-2 size-5 text-blue-600" />
              IP Público Fixo
            </h2>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${getPublicIPStatusColor(publicIp.status)}`}
            >
              {getPublicIPStatusLabel(publicIp.status)}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="text-xs font-medium text-gray-500">Endereço IPv4</label>
              <p className="font-mono text-lg font-semibold break-all text-gray-900">
                {publicIp.ip_address}
              </p>
            </div>
            {publicIp.gateway && (
              <div>
                <label className="text-xs font-medium text-gray-500">Gateway</label>
                <p className="mt-1 font-mono text-sm break-all text-gray-900">{publicIp.gateway}</p>
              </div>
            )}
            {publicIp.netmask && (
              <div>
                <label className="text-xs font-medium text-gray-500">Máscara</label>
                <p className="mt-1 font-mono text-sm break-all text-gray-900">{publicIp.netmask}</p>
              </div>
            )}
          </div>

          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <div className="flex gap-2">
              <InfoIcon className="mt-0.5 size-4 shrink-0 text-blue-600" />
              <div className="space-y-1 text-xs text-blue-800">
                <p>
                  <strong>Este IPv4 é reservado para você.</strong> Ele continua seu mesmo se esta
                  VM for excluída — e segue reservado
                  {publicIpPrice ? ' (e cobrado)' : ''} até que você o libere em Rede → IPs
                  Públicos, o que só é possível depois de excluir esta VM.
                </p>
                {publicIpPrice && (
                  <p>
                    Cobrança: <span className="font-semibold">{publicIpPrice}</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="mt-3">
            <Link to="/networking?tab=my-ips" className="text-sm text-blue-600 hover:underline">
              Gerenciar em Rede → IPs Públicos
            </Link>
          </div>
        </div>
      )}

      {/* Network Interfaces (NICs) */}
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center text-lg font-semibold text-gray-900">
            <NetworkIcon className="mr-2 size-5 text-purple-600" />
            Interfaces de Rede
            {nicsData && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({nicsData.total}/{nicsData.max_nics})
              </span>
            )}
          </h2>
          {!showAddNIC && (
            <button
              onClick={() => setShowAddNIC(true)}
              className="flex items-center gap-2 rounded-lg bg-purple-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-purple-700"
            >
              <PlusIcon className="size-4" />
              Adicionar Interface
            </button>
          )}
        </div>

        {/* Add NIC inline form */}
        {showAddNIC && (
          <div className="mb-4 rounded-lg border border-purple-200 bg-purple-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-medium text-purple-900">Nova Interface de Rede</h3>
              <button
                onClick={() => {
                  setShowAddNIC(false)
                  setSelectedVPCId('')
                  addNICMutation.reset()
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="size-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  VPC (Rede Virtual)
                </label>
                <select
                  value={selectedVPCId}
                  onChange={(e) => setSelectedVPCId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Selecione uma VPC...</option>
                  {vpcsData?.vpcs?.map((vpc: VPCListItem) => (
                    <option key={vpc.id} value={vpc.id} disabled={!vpc.enabled}>
                      {vpc.name} ({vpc.cidr}){vpc.enabled ? '' : ' - Desativada'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={nicFirewall}
                    onChange={(e) => setNicFirewall(e.target.checked)}
                    className="peer sr-only"
                  />
                  <div className="peer h-5 w-9 rounded-full bg-gray-200 peer-checked:bg-purple-600 peer-focus:ring-2 peer-focus:ring-purple-300 peer-focus:outline-none after:absolute after:top-[2px] after:left-[2px] after:size-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
                </label>
                <span className="text-sm text-gray-700">Firewall habilitado</span>
              </div>

              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <div className="flex gap-2">
                  <AlertCircleIcon className="mt-0.5 size-4 shrink-0 text-yellow-600" />
                  <p className="text-xs text-yellow-700">
                    A VM será parada e reiniciada automaticamente para aplicar a alteração.
                  </p>
                </div>
              </div>

              {addNICMutation.isError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <p className="text-sm text-red-700">
                    {(addNICMutation.error as any)?.response?.data?.detail ||
                      'Erro ao adicionar interface'}
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowAddNIC(false)
                    setSelectedVPCId('')
                    addNICMutation.reset()
                  }}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddNIC}
                  disabled={!selectedVPCId || addNICMutation.isPending}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-purple-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {addNICMutation.isPending ? (
                    <>
                      <Loader2Icon className="size-4 animate-spin" />
                      Adicionando...
                    </>
                  ) : (
                    'Adicionar'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* NICs list */}
        {nicsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2Icon className="size-6 animate-spin text-purple-600" />
            <span className="ml-2 text-sm text-gray-500">Carregando interfaces...</span>
          </div>
        ) : nicsData && nicsData.nics.length > 0 ? (
          <div className="space-y-3">
            {nicsData.nics.map((nic: VMNICInfo) => (
              <div key={nic.net_id}>
                {editingNic === nic.net_id ? (
                  /* Edit mode */
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="text-sm font-medium text-amber-900">
                        Editar {nic.net_id}
                        {nic.is_primary && (
                          <span className="ml-2 rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                            Primaria
                          </span>
                        )}
                      </h3>
                      <button
                        onClick={handleCancelEdit}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <XIcon className="size-4" />
                      </button>
                    </div>

                    <div className="space-y-3">
                      {nic.is_public ? (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <p className="text-xs text-gray-600">
                            Esta interface usa a <strong>rede pública</strong>, ligada ao seu IPv4
                            fixo. Trocá-la por uma VPC deixaria o IP sem uso e ainda reservado —
                            para abrir mão dele, libere-o em Rede → IPs Públicos. Aqui só o firewall
                            é editável.
                          </p>
                        </div>
                      ) : (
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            VPC (Rede Virtual)
                          </label>
                          <select
                            value={editVPCId}
                            onChange={(e) => setEditVPCId(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="">
                              Manter atual ({nic.vpc?.name || nic.bridge || '—'})
                            </option>
                            {vpcsData?.vpcs?.map((vpc: VPCListItem) => (
                              <option key={vpc.id} value={vpc.id} disabled={!vpc.enabled}>
                                {vpc.name} ({vpc.cidr}){vpc.enabled ? '' : ' - Desativada'}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <label className="relative inline-flex cursor-pointer items-center">
                          <input
                            type="checkbox"
                            checked={editFirewall}
                            onChange={(e) => setEditFirewall(e.target.checked)}
                            className="peer sr-only"
                          />
                          <div className="peer h-5 w-9 rounded-full bg-gray-200 peer-checked:bg-amber-600 peer-focus:ring-2 peer-focus:ring-amber-300 peer-focus:outline-none after:absolute after:top-[2px] after:left-[2px] after:size-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
                        </label>
                        <span className="text-sm text-gray-700">Firewall habilitado</span>
                      </div>

                      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                        <div className="flex gap-2">
                          <AlertCircleIcon className="mt-0.5 size-4 shrink-0 text-yellow-600" />
                          <p className="text-xs text-yellow-700">
                            A VM será parada e reiniciada automaticamente para aplicar a alteração.
                          </p>
                        </div>
                      </div>

                      {updateNICMutation.isError && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                          <p className="text-sm text-red-700">
                            {(updateNICMutation.error as any)?.response?.data?.detail ||
                              'Erro ao atualizar interface'}
                          </p>
                        </div>
                      )}

                      <div className="flex gap-2">
                        <button
                          onClick={handleCancelEdit}
                          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={handleSaveEdit}
                          disabled={updateNICMutation.isPending}
                          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-amber-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                        >
                          {updateNICMutation.isPending ? (
                            <>
                              <Loader2Icon className="size-4 animate-spin" />
                              Salvando...
                            </>
                          ) : (
                            'Salvar'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* View mode */
                  <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
                    <div className="flex min-w-0 flex-1 items-center space-x-3">
                      <div className="rounded-lg bg-purple-100 p-2">
                        <GlobeIcon className="size-5 text-purple-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900">{nic.net_id}</p>
                          {nic.is_primary && (
                            <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                              Primaria
                            </span>
                          )}
                          <span title={nic.firewall ? 'Firewall ativo' : 'Firewall desativado'}>
                            {nic.firewall ? (
                              <ShieldIcon className="size-3.5 text-green-600" />
                            ) : (
                              <ShieldOffIcon className="size-3.5 text-gray-400" />
                            )}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                          {nic.mac_address && <span className="font-mono">{nic.mac_address}</span>}
                          {nic.is_public ? (
                            <span className="rounded bg-blue-50 px-1.5 py-0.5 text-blue-700">
                              Rede pública
                            </span>
                          ) : nic.vpc ? (
                            <span className="rounded bg-cyan-50 px-1.5 py-0.5 text-cyan-700">
                              {nic.vpc.name}
                            </span>
                          ) : nic.bridge ? (
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                              {nic.bridge}
                            </span>
                          ) : null}
                        </div>
                        {nic.ip_addresses.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {nic.ip_addresses.map((ip, idx) => (
                              <span key={idx} className="font-mono text-sm text-gray-900">
                                {ip}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-3 flex items-center gap-2">
                      <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                        Ativo
                      </span>
                      <button
                        onClick={() => handleStartEdit(nic)}
                        className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-200"
                        title="Editar interface"
                      >
                        <PencilIcon className="size-4" />
                      </button>
                      {!nic.is_primary && (
                        <button
                          onClick={() => handleRemoveNIC(nic.net_id)}
                          disabled={removingNic === nic.net_id}
                          className="rounded-lg p-1.5 text-red-500 transition-colors hover:bg-red-50 disabled:opacity-50"
                          title="Remover interface"
                        >
                          {removingNic === nic.net_id ? (
                            <Loader2Icon className="size-4 animate-spin" />
                          ) : (
                            <Trash2Icon className="size-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500">
            <NetworkIcon className="mx-auto mb-3 size-12 text-gray-300" />
            <p>Nenhuma interface de rede encontrada</p>
            <p className="mt-1 text-sm">Certifique-se de que a VM possui configuração de rede</p>
          </div>
        )}

        {removeNICMutation.isError && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-700">
              {(removeNICMutation.error as any)?.response?.data?.detail ||
                'Erro ao remover interface'}
            </p>
          </div>
        )}
      </div>

      {/* Static IP Configuration */}
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center text-lg font-semibold text-gray-900">
            <SettingsIcon className="mr-2 size-5 text-cyan-600" />
            Configurar IP Fixo
          </h2>
          {!showConfig && !configSuccess && (
            <button
              onClick={() => setShowConfig(true)}
              className="rounded-lg bg-cyan-600 px-4 py-2 text-sm text-white transition-colors hover:bg-cyan-700"
            >
              Configurar
            </button>
          )}
        </div>

        {!showConfig && !configSuccess && (
          <div className="rounded-lg bg-gray-50 p-4">
            <p className="text-sm text-gray-600">
              Configure um IP fixo para esta VM usando cloud-init. A VM precisa ter cloud-init
              instalado e será necessário reinicia-la após a configuração.
            </p>
          </div>
        )}

        {configSuccess && configResult && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg bg-green-50 p-4">
              <CheckCircleIcon className="size-6 text-green-600" />
              <div>
                <p className="font-medium text-green-900">Configurado com sucesso!</p>
                <p className="text-sm text-green-700">Configuração de rede aplicada à VM</p>
              </div>
            </div>

            <div className="space-y-2 rounded-lg bg-gray-50 p-4">
              <h4 className="font-medium text-gray-900">Configuracao aplicada:</h4>
              <div className="space-y-1 font-mono text-sm">
                <p>
                  <span className="text-gray-500">IP:</span>{' '}
                  {configResult?.network_config?.ip_address}
                </p>
                <p>
                  <span className="text-gray-500">Gateway:</span>{' '}
                  {configResult?.network_config?.gateway}
                </p>
                <p>
                  <span className="text-gray-500">DNS:</span>{' '}
                  {configResult?.network_config?.dns_servers?.join(', ')}
                </p>
                <p>
                  <span className="text-gray-500">Hostname:</span>{' '}
                  {configResult?.network_config?.hostname}
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <div className="flex gap-3">
                <AlertCircleIcon className="mt-0.5 size-5 shrink-0 text-yellow-600" />
                <div>
                  <p className="font-medium text-yellow-900">Proximos passos:</p>
                  <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-yellow-700">
                    {configResult?.next_steps?.map((step: string, i: number) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleReset}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
              >
                Nova Configuracao
              </button>
              <button
                onClick={() => removeCloudInitMutation.mutate()}
                disabled={removeCloudInitMutation.isPending}
                className="flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-white transition-colors hover:bg-orange-700 disabled:opacity-50"
              >
                {removeCloudInitMutation.isPending ? (
                  <Loader2Icon className="size-4 animate-spin" />
                ) : (
                  <Trash2Icon className="size-4" />
                )}
                Resetar para DHCP
              </button>
            </div>
          </div>
        )}

        {showConfig && !configSuccess && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex gap-3">
                <AlertCircleIcon className="mt-0.5 size-5 shrink-0 text-blue-600" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium">Requisitos:</p>
                  <ul className="mt-1 list-inside list-disc">
                    <li>VM deve ter sido criada de um template com cloud-init</li>
                    <li>Reinicie a VM após configurar para aplicar as mudancas</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Endereco IP (com mascara) *
                </label>
                <input
                  type="text"
                  value={config.ip_address}
                  onChange={(e) => setConfig({ ...config, ip_address: e.target.value })}
                  placeholder="10.0.1.100/24"
                  className={`w-full rounded-lg border px-4 py-2 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500 ${
                    config.ip_address && !isValidCIDR(config.ip_address)
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                  }`}
                />
                {config.ip_address && !isValidCIDR(config.ip_address) && (
                  <p className="mt-1 text-sm text-red-600">Formato: 10.0.1.100/24</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Gateway *</label>
                <input
                  type="text"
                  value={config.gateway}
                  onChange={(e) => setConfig({ ...config, gateway: e.target.value })}
                  placeholder="10.0.1.1"
                  className={`w-full rounded-lg border px-4 py-2 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500 ${
                    config.gateway && !isValidIP(config.gateway)
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                  }`}
                />
                {config.gateway && !isValidIP(config.gateway) && (
                  <p className="mt-1 text-sm text-red-600">IP invalido</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Servidores DNS
                </label>
                <input
                  type="text"
                  value={dnsInput}
                  onChange={(e) => setDnsInput(e.target.value)}
                  placeholder="8.8.8.8, 8.8.4.4"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500"
                />
                <p className="mt-1 text-xs text-gray-500">Separados por virgula</p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Interface de Rede
                </label>
                <select
                  value={config.interface}
                  onChange={(e) => setConfig({ ...config, interface: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="eth0">eth0</option>
                  <option value="ens18">ens18</option>
                  <option value="ens19">ens19</option>
                  <option value="enp0s3">enp0s3</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Hostname (opcional)
                </label>
                <input
                  type="text"
                  value={config.hostname}
                  onChange={(e) => setConfig({ ...config, hostname: e.target.value })}
                  placeholder="meu-servidor"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>

            {configureIPMutation.isError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <div className="flex gap-3">
                  <AlertCircleIcon className="size-5 shrink-0 text-red-600" />
                  <p className="text-sm text-red-700">
                    {(configureIPMutation.error as any)?.response?.data?.detail ||
                      'Erro ao configurar IP. Verifique os dados e tente novamente.'}
                  </p>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleReset}
                className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!isFormValid() || configureIPMutation.isPending}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-white transition-colors hover:bg-cyan-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {configureIPMutation.isPending ? (
                  <>
                    <Loader2Icon className="size-4 animate-spin" />
                    Configurando...
                  </>
                ) : (
                  'Aplicar Configuracao'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
